// src/pages/approval/LeaveRequestNew.jsx
// ✅ 휴가신청서(연차) 작성(2026-10-19). 소요시간·차감·잔여 스냅샷·문서번호는 전부 서버가 계산한다
// (GET /draft/leave-requests/preview/ — 캘린더 차감과 같은 leave_hours 공식). 화면은 입력만 모은다.
// 신청 = 저장 + 상신. 결재선(기안 → 윤길상 팀장, 본인이면 전결)과 경영팀 자동 수신참조는 서버가 만든다.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { parseLeavePrefill } from "../../utils/leavePrefill";
import api from "../../api";
import { isActiveStaff } from "../../utils/contractEnded";
import LeaveRequestPreview, { buildLeaveRequestProps } from "../../components/preview/leave/LeaveRequestPreview";
import HoursText from "../../components/preview/leave/HoursText";
import styles from "./LeaveRequest.module.css";

export const LEAVE_KINDS = [
  { value: "full", label: "종일휴가" },
  { value: "half_am", label: "반차(오전)" },
  { value: "half_pm", label: "반차(오후)" },
  { value: "hourly", label: "시간차" },
];
const BREAKS = [
  { value: "0", label: "없음" },
  { value: "0.5", label: "30분" },
  { value: "1", label: "1시간" },
];

const todayIso = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

export default function LeaveRequestNew() {
  const navigate = useNavigate();
  const me = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; }
  }, []);

  const [kind, setKind] = useState("full");
  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(todayIso());
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("11:00");
  const [breakHours, setBreakHours] = useState("0");
  const [reason, setReason] = useState("");
  const [staff, setStaff] = useState([]);
  const [ccPick, setCcPick] = useState("");
  const [ccList, setCcList] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reload, setReload] = useState(0);   // 신청 실패 뒤 잔여·번호 다시 계산

  // ✅ 캘린더 바로가기 프리필(?kind&start_date…, utils/leavePrefill) — 탭이 살아 있어도 새 쿼리로 오면 다시 채운다.
  // 다른 화면으로 옮겨 간 동안(숨은 탭)의 주소는 무시한다.
  const location = useLocation();
  const appliedSearch = useRef("");
  useEffect(() => {
    if (location.pathname !== "/approval/leave/new" || appliedSearch.current === location.search) return;
    appliedSearch.current = location.search;
    const p = parseLeavePrefill(location.search);
    if (!p) return;
    setKind(p.kind);
    setStartDate(p.startDate);
    setEndDate(p.endDate);
    if (p.startTime) {
      setStartTime(p.startTime);
      setEndTime(p.endTime);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    api.get("/staff-contacts/")
      .then(({ data }) => {
        const list = (Array.isArray(data) ? data : data.results || [])
          .filter(isActiveStaff)
          .map((u) => ({ id: u.user_id || u.id, name: u.name, position: u.position || "" }))
          .filter((u) => u.id && u.id !== me.id);
        setStaff(list);
      })
      .catch(() => setStaff([]));
  }, [me.id]);

  const params = useMemo(() => {
    const p = { kind, start_date: startDate, end_date: kind === "full" ? endDate : startDate };
    if (kind === "hourly") Object.assign(p, { start_time: startTime, end_time: endTime, break_hours: breakHours });
    return p;
  }, [kind, startDate, endDate, startTime, endTime, breakHours]);

  // 입력이 바뀔 때마다 서버 계산(살짝 늦춰서 — 날짜를 타이핑하는 동안 매번 부르지 않게)
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      api.get("/draft/leave-requests/preview/", { params })
        .then(({ data }) => { if (!cancelled) { setPreview(data); setPreviewError(""); } })
        .catch((err) => {
          if (cancelled) return;
          setPreview(null);
          setPreviewError(err.response?.data?.error || "계산하지 못했습니다.");
        });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [params, reload]);

  const addCc = () => {
    const u = staff.find((s) => String(s.id) === ccPick);
    if (u && !ccList.some((c) => c.id === u.id)) setCcList((prev) => [...prev, u]);
    setCcPick("");
  };

  const canSubmit = preview && preview.sufficient && !previewError && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const isSelf = preview.approver?.is_self;
    const ask = isSelf
      ? `${preview.kind_label} ${preview.hours_text}를 신청합니다. 결재자 본인이라 전결로 바로 승인되고 캘린더에 등록됩니다.`
      : `${preview.kind_label} ${preview.hours_text}를 신청합니다. 결재자: ${preview.approver?.name || ""}`;
    if (!window.confirm(ask)) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/draft/leave-requests/", {
        ...params,
        reason,
        reference_ids: ccList.map((c) => c.id),
        draft_no: preview.draft_no_preview,
      });
      const no = data.draft_number?.draft_no || data.document?.draft_no;
      const state = data.document?.leave_state_label || "";
      alert(`${no}로 신청되었습니다.${state === "승인" ? " (전결 승인 · 캘린더 등록 완료)" : ""}`);
      navigate("/approval/leave/list");
    } catch (err) {
      alert(err.response?.data?.error || "신청하지 못했습니다.");
      // 잔여·번호가 바뀌었을 수 있다 — 다시 계산
      setReload((n) => n + 1);
    } finally {
      setSubmitting(false);
    }
  };

  const draftDoc = preview
    ? { ...preview, reason: reason.trim() || "개인 사정", chosen_cc: ccList.map((c) => c.name) }
    : null;

  return (
    <div className={styles.page}>
      <div className={styles.layout}>
        <div className={styles.formCard}>
          <h2 className={styles.title}>휴가신청서 작성</h2>

          <div className={styles.row}>
            <span className={styles.label}>기안자</span>
            <span>{[me.position, me.name].filter(Boolean).join(" ") || "-"}</span>
          </div>

          {/* 버튼은 라벨과 분리된 묶음 안에서만 줄바꿈 — 둘째 줄도 라벨 오른쪽 열에서 시작(2026-10-22) */}
          <div className={`${styles.row} ${styles.rowTop}`}>
            <span className={`${styles.label} ${styles.labelTop}`}>휴가구분</span>
            <div className={styles.kindGroup} role="group" aria-label="휴가구분">
              {LEAVE_KINDS.map((k) => (
                <button key={k.value} type="button" onClick={() => setKind(k.value)}
                        className={kind === k.value ? styles.kindBtnActive : styles.kindBtn}>
                  {k.label}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.row}>
            <span className={styles.label}>{kind === "full" ? "시작일자" : "휴가일자"}</span>
            <input type="date" className={styles.input} value={startDate}
                   onChange={(e) => { setStartDate(e.target.value); if (endDate < e.target.value) setEndDate(e.target.value); }} />
          </div>
          {kind === "full" && (
            <div className={styles.row}>
              <span className={styles.label}>종료일자</span>
              <input type="date" className={styles.input} value={endDate} min={startDate}
                     onChange={(e) => setEndDate(e.target.value)} />
            </div>
          )}
          {kind === "hourly" && (
            <>
              <div className={styles.row}>
                <span className={styles.label}>시간</span>
                <input type="time" step={600} className={styles.input} style={{ width: 110 }} value={startTime}
                       onChange={(e) => setStartTime(e.target.value)} />
                <span>~</span>
                <input type="time" step={600} className={styles.input} style={{ width: 110 }} value={endTime}
                       onChange={(e) => setEndTime(e.target.value)} />
              </div>
              <div className={styles.row}>
                <span className={styles.label}>휴게시간</span>
                <select className={styles.select} value={breakHours} onChange={(e) => setBreakHours(e.target.value)}>
                  {BREAKS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                </select>
              </div>
            </>
          )}
          {kind !== "hourly" && (
            <div className={styles.hint}>
              {kind === "full" ? "09:00~18:00 · 휴게 1시간 = 하루 8시간" : kind === "half_am" ? "09:00~13:00 = 4시간" : "14:00~18:00 = 4시간"}
            </div>
          )}

          <div className={styles.row}>
            <span className={styles.label}>휴가사유</span>
            <input className={`${styles.input} ${styles.wide}`} value={reason} maxLength={500}
                   placeholder="개인 사정" onChange={(e) => setReason(e.target.value)} />
          </div>

          <div className={styles.row}>
            <span className={styles.label}>수신참조</span>
            <select className={styles.select} value={ccPick} onChange={(e) => setCcPick(e.target.value)}>
              <option value="">직원 선택(선택사항)</option>
              {staff.filter((s) => !ccList.some((c) => c.id === s.id)).map((s) => (
                <option key={s.id} value={s.id}>{[s.name, s.position].filter(Boolean).join(" ")}</option>
              ))}
            </select>
            <button type="button" className={styles.smallBtn} onClick={addCc} disabled={!ccPick}>추가</button>
          </div>
          {ccList.length > 0 && (
            <div className={styles.chips}>
              {ccList.map((c) => (
                <span key={c.id} className={styles.chip}>
                  {c.name}
                  <button type="button" className={styles.chipX} aria-label={`${c.name} 빼기`}
                          onClick={() => setCcList((prev) => prev.filter((x) => x.id !== c.id))}>✕</button>
                </span>
              ))}
            </div>
          )}
          {preview?.auto_cc?.length > 0 && (
            <div className={styles.hint}>경영팀 {preview.auto_cc.join(", ")}은(는) 자동으로 수신참조됩니다.</div>
          )}

          <div className={styles.calcBox}>
            <div className={styles.calcLine}>
              <span>소요시간</span>
              <span>{preview ? `근무일 ${preview.working_days.length}일 × ${preview.per_day_hours}시간` : "-"}</span>
            </div>
            <div className={styles.calcLine}>
              <span>휴가차감</span><HoursText text={preview?.hours_text} strong align="end" />
            </div>
            <div className={styles.calcLine}>
              <span>잔여(사용 가능)</span><HoursText text={preview?.snapshot.texts.available} align="end" />
            </div>
            <div className={styles.calcLine}>
              <span>차감 후 잔여</span><HoursText text={preview?.snapshot.texts.after} align="end" />
            </div>
          </div>

          {previewError && <div className={styles.error}>{previewError}</div>}
          {preview && !preview.sufficient && <div className={styles.error}>{preview.message}</div>}

          <button type="button" className={styles.submitBtn} disabled={!canSubmit} onClick={handleSubmit}>
            {submitting ? "신청 중..." : preview?.approver?.is_self ? "신청(전결)" : "신청"}
          </button>
        </div>

        <div className={styles.previewCol}>
          {draftDoc ? <LeaveRequestPreview {...buildLeaveRequestProps(draftDoc)} /> : null}
        </div>
      </div>
    </div>
  );
}
